// VoIP API Service - Real API calls for VoIP Dashboard
import axios from 'axios';
import { APP_BASE_URL, GET_LATEST_TELEMETRY, GET_DEVICE_BY_CUSTOMER_ID, GET_UNIVERSAL_DEVICE_KEYS, GET_ASSETS_PAGINATED, trimAssetName } from '../../../Common/EndPoints';
import { VoIPDevice, VoIPAlert, CallRecord } from './voipTypes';
import moment from 'moment';

// Interface for device info from /v2/device API
export interface DeviceAssetInfo {
  deviceId: string;
  name: string;
  vendor: string;
  make: string;
  model: string;
  serialNumber: string;
  customerId: string;
  gearType: string;
  systemCode: string;
  isActive: boolean;
  additionaldetail: Record<string, any>;
}

// Get the VoIP system ID from systems list
export const getVoIPSystemId = (): string | null => {
  try {
    const systemsList = JSON.parse(localStorage.getItem('systems_list') || '[]');
    const voipSystem = systemsList.find((sys: any) =>
      sys.code?.toUpperCase() === 'VOIP' ||
      sys.name?.toUpperCase().includes('VOIP')
    );
    return voipSystem?.id || null;
  } catch {
    return null;
  }
};

// Get customer IDs from context or localStorage
export const getCustomerIds = (): string[] => {
  try {
    const customersList = JSON.parse(localStorage.getItem('selected_customers') || '[]');
    if (customersList.length > 0) {
      return customersList.map((c: any) => c.customerId || c.id);
    }
    // Fallback to permissions
    const permissions = JSON.parse(localStorage.getItem('permissions') || '{}');
    if (permissions.customerIds) {
      return permissions.customerIds;
    }
    return [];
  } catch {
    return [];
  }
};

// Fetch VoIP device types
export const fetchVoIPDeviceTypes = async (systemId: string, customerIds: string[]): Promise<any[]> => {
  try {
    const url = `${APP_BASE_URL}${GET_DEVICE_BY_CUSTOMER_ID}`;
    const params = { softwareIds: systemId };
    const response = await axios.post(url, customerIds, { params });
    return response?.data?.data || [];
  } catch (error) {
    console.error('Error fetching VoIP device types:', error);
    return [];
  }
};

// Fetch device keys/columns for a specific device type
export const fetchDeviceKeys = async (deviceType: string, customerIds: string[]): Promise<any[]> => {
  try {
    const url = `${APP_BASE_URL}${GET_UNIVERSAL_DEVICE_KEYS}?customerIds=${customerIds.toString()}&type=${deviceType}`;
    const response = await axios.get(url);
    return response?.data?.data || [];
  } catch (error) {
    console.error('Error fetching device keys:', error);
    return [];
  }
};

/**
 * Fetch VoIP device assets from /v2/device API
 * This API returns device info including vendor, make, model from additionaldetail
 * @param systemId - VoIP system ID
 * @param customerIds - Customer IDs to filter by
 * @returns Map of deviceId -> DeviceAssetInfo for quick lookup
 */
export const fetchVoIPDeviceAssets = async (
  systemId: string,
  customerIds: string[]
): Promise<Map<string, DeviceAssetInfo>> => {
  const deviceMap = new Map<string, DeviceAssetInfo>();

  try {
    const params = {
      customerIds: customerIds.toString(),
      softwareIds: systemId,
      with_type: true,
      is_active: true,
      pageSize: 1000, // Get all VoIP devices
      page: 1,
    };

    const response = await axios.get(`${APP_BASE_URL}${GET_ASSETS_PAGINATED}`, { params });
    const devices = response?.data?.data?.content || [];

    devices.forEach((device: any) => {
      const additionaldetail = device.additionaldetail || {};
      const deviceInfo: DeviceAssetInfo = {
        deviceId: device.deviceid?.toString() || '',
        name: device.name || '',
        // Get vendor from additionaldetail - this is where CORAL/BITCOMM vendor is stored
        vendor: additionaldetail.vendor || additionaldetail.Vendor || additionaldetail.VENDOR || '',
        make: additionaldetail.make || additionaldetail.Make || additionaldetail.MAKE || '',
        model: additionaldetail.model || additionaldetail.Model || additionaldetail.MODEL || '',
        serialNumber: additionaldetail.serialNumber || additionaldetail.serial_number || '',
        customerId: device.customer_id || '',
        gearType: device.deviceType?.label || '',
        systemCode: device.software?.code || '',
        isActive: device.isactive || false,
        additionaldetail: additionaldetail,
      };

      if (deviceInfo.deviceId) {
        deviceMap.set(deviceInfo.deviceId, deviceInfo);
      }
    });

    console.log(`Fetched ${deviceMap.size} VoIP device assets with vendor info`);
  } catch (error) {
    console.error('Error fetching VoIP device assets:', error);
  }

  return deviceMap;
};

// Parse device name to extract Zone, Division, Station, Device Name
// Format: ZONE_DIVISION_STATION_DEVICENAME (e.g., NCR_PRYJ_PRYJSTN_PHONE1)
// Same logic as Telemetry page - split ORIGINAL device name by underscore
const parseDeviceName = (fullName: string) => {
  // Split the ORIGINAL device name by underscore (same as Telemetry page)
  const parts = fullName.split('_');
  const trimmedName = trimAssetName(fullName); // Use trimmed name for display

  let zone = '-';
  let division = '-';
  let station = '-';
  let deviceName = trimmedName;

  if (parts.length >= 4) {
    // Format: ZONE_DIVISION_STATION_DEVICENAME
    // Index [0] = Zone, Index [1] = Division, Index [2] = Station
    zone = parts[0] || '-';
    division = parts[1] || '-';
    station = parts[2] || '-';
    // Device name is everything after station (index 3+)
    deviceName = parts.slice(3).join('-') || trimmedName;
  } else if (parts.length === 3) {
    // Format: ZONE_DIVISION_STATION or DIVISION_STATION_DEVICENAME
    zone = parts[0] || '-';
    division = parts[1] || '-';
    station = parts[2] || '-';
  } else if (parts.length === 2) {
    // Format: STATION_DEVICENAME
    station = parts[0] || '-';
    deviceName = parts[1] || trimmedName;
  }

  return { zone, division, station, deviceName };
};

// Detect vendor type from device asset info (from /v2/device API) or telemetry data
const detectVendor = (
  deviceType?: string,
  rawData?: any,
  mergedData?: Record<string, any>,
  deviceAssetInfo?: DeviceAssetInfo | null
): 'BITCOMM' | 'CORAL' | 'UNKNOWN' => {
  // Priority 0 (HIGHEST): Check vendor from /v2/device API (additionaldetail.vendor)
  // This is the most reliable source as it comes from the device registration
  if (deviceAssetInfo) {
    const assetVendor = (deviceAssetInfo.vendor || '').toUpperCase();
    if (assetVendor.includes('CORAL')) return 'CORAL';
    if (assetVendor.includes('BITCOMM')) return 'BITCOMM';

    // Also check make field from device API
    const assetMake = (deviceAssetInfo.make || '').toUpperCase();
    if (assetMake.includes('CORAL')) return 'CORAL';
    if (assetMake.includes('BITCOMM')) return 'BITCOMM';
  }

  // Priority 1: Check asset.vendor field (from telemetry merged data)
  // Note: asset.vendor might be "VoIP" (system type) or actual vendor like "CORAL", "BITCOMM"
  const assetVendor = (mergedData?.['asset.vendor'] || '').toUpperCase();
  if (assetVendor.includes('CORAL')) return 'CORAL';
  if (assetVendor.includes('BITCOMM')) return 'BITCOMM';

  // Priority 2: Check dedicated VENDOR field (different from asset.vendor)
  const vendorField = (mergedData?.['VENDOR'] || mergedData?.['vendor'] || mergedData?.['Vendor'] || '').toUpperCase();
  if (vendorField.includes('CORAL')) return 'CORAL';
  if (vendorField.includes('BITCOMM')) return 'BITCOMM';

  // Priority 3: Check asset info nested fields from telemetry
  const assetMake = (mergedData?.['asset.make'] || mergedData?.['MAKE'] || mergedData?.['make'] || '').toUpperCase();
  if (assetMake.includes('CORAL')) return 'CORAL';
  if (assetMake.includes('BITCOMM')) return 'BITCOMM';

  // Priority 4: Check device type / label / gearType
  const type = (deviceType || rawData?.label || rawData?.gearType || mergedData?.['GEARTYPE'] || '').toUpperCase();
  if (type.includes('BITCOMM')) return 'BITCOMM';
  if (type.includes('CORAL')) return 'CORAL';

  // Priority 5: Check in dataLists for vendor hints
  const dataLists = rawData?.dataLists || [];
  for (const item of dataLists) {
    const data = item?.data || {};
    // Check vendor field in each data item - handle array format from API
    const vendorVal = data['asset.vendor']?.[0]?.value || data['VENDOR']?.[0]?.value ||
                      data['vendor']?.[0]?.value || data['Vendor']?.[0]?.value ||
                      data['asset.make']?.[0]?.value || data['MAKE']?.[0]?.value || '';
    if (vendorVal.toUpperCase().includes('CORAL')) return 'CORAL';
    if (vendorVal.toUpperCase().includes('BITCOMM')) return 'BITCOMM';
    // CORAL specific fields presence
    if (data['CORAL_STATUS'] || data['coral_status'] || data['CoralStatus']) return 'CORAL';
    // BITCOMM specific fields presence
    if (data['BITCOMM_STATUS'] || data['bitcomm_status'] || data['PORT_STATUS']) return 'BITCOMM';
  }

  // Priority 6: Check device name for vendor hints
  const deviceName = (rawData?.deviceName || mergedData?.['NAME'] || '').toUpperCase();
  if (deviceName.includes('CORAL')) return 'CORAL';
  if (deviceName.includes('BITCOMM')) return 'BITCOMM';

  return 'UNKNOWN';
};

// Parse numeric values safely
const parseNum = (val: any, def: number = 0): number => {
  const num = Number(val);
  return isNaN(num) ? def : num;
};

// Transform BITCOMM vendor data
const transformBitcommData = (rawData: any, mergedData: Record<string, any>, index: number): Partial<VoIPDevice> => {
  // BITCOMM specific field mappings
  const ipAddress = mergedData['asset.ipAddress'] || mergedData['IP_ADDRESS'] || mergedData['IP'] || mergedData['IPADDRESS'] || '-';
  const macAddress = mergedData['MAC_ADDRESS'] || mergedData['MAC'] || mergedData['MAC_ADDR'] || '-';

  // BITCOMM status check - PORT_STATUS, LINK_STATUS fields
  const portStatus = (mergedData['PORT_STATUS'] || '').toString().toUpperCase();
  const linkStatus = (mergedData['LINK_STATUS'] || '').toString().toUpperCase();
  const assetHealth = (mergedData['asset.health'] || '').toString().toUpperCase();

  const isOnline = portStatus === 'UP' || portStatus === '1' ||
                   linkStatus === 'UP' || linkStatus === 'CONNECTED' ||
                   assetHealth === 'HEALTHY';

  // BITCOMM latency/performance fields
  const latency = parseNum(
    mergedData['PING_RTT'] || mergedData['RTT'] || mergedData['LATENCY'] || mergedData['RESPONSE_TIME'],
    0
  );

  return {
    ipAddress,
    macAddress,
    status: isOnline ? 'online' : 'offline',
    registrationStatus: isOnline ? 'registered' : 'unregistered',
    latency,
    jitter: parseNum(mergedData['JITTER'] || mergedData['PING_JITTER'], 0),
    packetLoss: parseNum(mergedData['PACKET_LOSS'] || mergedData['PKT_LOSS'] || mergedData['LOSS_PERCENT'], 0),
    firmware: mergedData['FIRMWARE'] || mergedData['FW_VERSION'] || mergedData['FW'] || '-',
    model: mergedData['MODEL'] || mergedData['DEVICE_MODEL'] || '-',
  };
};

// Transform CORAL vendor data
const transformCoralData = (rawData: any, mergedData: Record<string, any>, index: number): Partial<VoIPDevice> => {
  // CORAL specific field mappings - CORAL uses different naming conventions
  const ipAddress = mergedData['asset.ipAddress'] || mergedData['ipAddress'] || mergedData['ip_address'] || mergedData['IP'] || '-';
  const macAddress = mergedData['macAddress'] || mergedData['mac_address'] || mergedData['MAC'] || '-';

  // CORAL status check - uses different status field names
  const coralStatus = (mergedData['CORAL_STATUS'] || mergedData['coral_status'] || mergedData['status'] || '').toString().toUpperCase();
  const deviceStatus = (mergedData['DEVICE_STATUS'] || mergedData['device_status'] || '').toString().toUpperCase();
  const assetHealth = (mergedData['asset.health'] || '').toString().toUpperCase();
  const alertInfo = mergedData['alertInfo'] || {};
  const alertHealth = (alertInfo.health || '').toString().toUpperCase();

  const isOnline = coralStatus === 'ONLINE' || coralStatus === 'ACTIVE' || coralStatus === 'UP' ||
                   deviceStatus === 'ONLINE' || deviceStatus === 'ACTIVE' ||
                   assetHealth === 'HEALTHY' || alertHealth === 'HEALTHY';

  // CORAL latency/performance fields - may use different field names
  const latency = parseNum(
    mergedData['latency'] || mergedData['rtt'] || mergedData['LATENCY'] || mergedData['RTT'] || mergedData['response_time'],
    0
  );

  return {
    ipAddress,
    macAddress,
    status: isOnline ? 'online' : 'offline',
    registrationStatus: isOnline ? 'registered' : 'unregistered',
    latency,
    jitter: parseNum(mergedData['jitter'] || mergedData['JITTER'], 0),
    packetLoss: parseNum(mergedData['packet_loss'] || mergedData['packetLoss'] || mergedData['PACKET_LOSS'], 0),
    firmware: mergedData['firmware'] || mergedData['fw_version'] || mergedData['FIRMWARE'] || '-',
    model: mergedData['model'] || mergedData['MODEL'] || mergedData['device_model'] || '-',
  };
};

// Transform raw telemetry data to VoIPDevice format (supports BITCOMM and CORAL vendors)
const transformTelemetryToVoIPDevice = (
  rawData: any,
  index: number,
  deviceType?: string,
  deviceAssetMap?: Map<string, DeviceAssetInfo>
): VoIPDevice => {
  const fullDeviceName = rawData.deviceName || '';
  const { zone, division, station, deviceName: parsedDeviceName } = parseDeviceName(fullDeviceName);
  const dataLists = rawData.dataLists || [];

  // Get device asset info from the /v2/device API (contains vendor from additionaldetail)
  const deviceId = rawData.deviceId?.toString() || '';
  const deviceAssetInfo = deviceAssetMap?.get(deviceId) || null;

  // Get station name from API response (customerName field) - this comes from Station API
  const stationFromApi = rawData.customerName || rawData.customer_name || rawData.stationName || '';

  // Merge all data packets from the API response
  let mergedData: Record<string, any> = {};
  let lastActivityTime = rawData.ts || 0;

  const keyTimestamps: Record<string, number> = {};
  dataLists.forEach((item: any) => {
    const data = item?.data || {};
    Object.entries(data).forEach(([key, value]: [string, any]) => {
      // Handle nested objects like additionalInfo and alertInfo
      const currentVal = value?.[0]?.value;
      const currentTs = Number(value?.[0]?.ts || 0);
      if (!keyTimestamps[key] || currentTs > keyTimestamps[key]) {
        keyTimestamps[key] = currentTs;

        if ((key === 'additionalInfo' || key === 'alertInfo') && typeof currentVal === 'object') {
          mergedData[key] = currentVal;
        } else {
          mergedData[key] = currentVal ?? '';
        }
      }

      if (currentTs > lastActivityTime) {
        lastActivityTime = currentTs;
      }
    });
  });

  // Detect vendor type - use device asset info from /v2/device API as highest priority
  const vendor = detectVendor(deviceType, rawData, mergedData, deviceAssetInfo);

  // Get vendor-specific transformed data
  let vendorData: Partial<VoIPDevice> = {};
  if (vendor === 'BITCOMM') {
    vendorData = transformBitcommData(rawData, mergedData, index);
  } else if (vendor === 'CORAL') {
    vendorData = transformCoralData(rawData, mergedData, index);
  } else {
    // Fallback - try both field naming conventions
    vendorData = {
      ...transformBitcommData(rawData, mergedData, index),
      ...transformCoralData(rawData, mergedData, index),
    };

    // Use generic status detection as fallback
    const assetHealth = (mergedData['asset.health'] || '').toString().toUpperCase();
    const alertInfo = mergedData['alertInfo'] || {};
    const alertHealth = (alertInfo.health || '').toString().toUpperCase();
    const alertSeverity = (alertInfo.severity || '').toString().toUpperCase();
    const alertIsClear = alertInfo.isClear === true;
    const responseMessage = mergedData['additionalInfo']?.responseMeta?.message || '';
    const messageContainsReachable = responseMessage.toLowerCase().includes('reachable');
    const messageContainsUnreachable = responseMessage.toLowerCase().includes('unreachable') ||
                                       responseMessage.toLowerCase().includes('not reachable');

    const isOnline = assetHealth === 'HEALTHY' ||
                     alertHealth === 'HEALTHY' ||
                     (alertSeverity === 'NORMAL' && alertIsClear) ||
                     (messageContainsReachable && !messageContainsUnreachable);

    vendorData.status = isOnline ? 'online' : 'offline';
    vendorData.registrationStatus = isOnline ? 'registered' : 'unregistered';
  }

  // Get actual device name - use trimmed asset name (parsedDeviceName) as primary
  // The NAME field in API often contains IP address, not the device name
  const actualDeviceName = parsedDeviceName || mergedData['device_name'] || mergedData['PARENT_DEVICE'] || `VoIP Device ${index + 1}`;

  // Get station from STN field in telemetry data (this is the station code like BPU, PRYJ, GZB-PA)
  // STN field is the most reliable source for station from telemetry API
  const finalStation = mergedData['STN'] || mergedData['STATION'] || mergedData['station'] || stationFromApi || station || 'Unknown';

  // Get zone and division from API fields or fallback to parsed device name
  const finalZone = rawData.zone || mergedData['asset.zone'] || mergedData['ZONE'] || mergedData['zone'] || zone || '-';
  const finalDivision = rawData.division || mergedData['asset.division'] || mergedData['DIVISION'] || mergedData['DIV'] || mergedData['division'] || division || '-';

  // Get gear type for display
  const gearType = mergedData['GEARTYPE'] || mergedData['gearType'] || rawData.label || '-';

  return {
    id: rawData.deviceId || `voip-${index}`,
    deviceId: rawData.deviceId || `DEV-${index}`,
    name: actualDeviceName,
    station: finalStation,
    // Get customerId from device assets API (most reliable) or fallback to telemetry data
    customerId: deviceAssetInfo?.customerId || rawData.customerId || rawData.customer_id || '',
    ipAddress: vendorData.ipAddress || '-',
    macAddress: vendorData.macAddress || '-',
    status: vendorData.status || 'offline',
    registrationStatus: vendorData.registrationStatus || 'unregistered',
    lastActivity: lastActivityTime > 0 ? moment(lastActivityTime).toISOString() : new Date().toISOString(),
    signalStrength: parseNum(mergedData['SIGNAL'] || mergedData['RSSI'] || mergedData['signal'], 0),
    latency: vendorData.latency || 0,
    jitter: vendorData.jitter || 0,
    packetLoss: vendorData.packetLoss || 0,
    firmware: vendorData.firmware || '-',
    model: vendorData.model || gearType || '-',
    uptime: parseNum(mergedData['UPTIME'] || mergedData['uptime'], 0),
    rxPackets: parseNum(mergedData['RX_PACKETS'] || mergedData['rx_packets'], 0),
    txPackets: parseNum(mergedData['TX_PACKETS'] || mergedData['tx_packets'], 0),
    assetDetails: {
      // Prioritize vendor from /v2/device API (deviceAssetInfo), then telemetry fields
      make: deviceAssetInfo?.vendor || deviceAssetInfo?.make ||
            mergedData['VENDOR'] || mergedData['Vendor'] || mergedData['vendor'] ||
            mergedData['MAKE'] || mergedData['make'] || mergedData['asset.make'] ||
            (mergedData['asset.vendor'] !== 'VoIP' ? mergedData['asset.vendor'] : '') || '-',
      model: deviceAssetInfo?.model || gearType || vendorData.model || '-',
      serialNumber: deviceAssetInfo?.serialNumber ||
                    mergedData['asset.clientAssetId'] || mergedData['SERIAL'] || mergedData['serial_number'] || '-',
      installationDate: mergedData['INSTALL_DATE'] || mergedData['install_date'] || '-',
      warrantyExpiry: mergedData['WARRANTY'] || mergedData['warranty'] || '-',
      location: mergedData['LOC'] || mergedData['LOCATION'] || mergedData['location'] || finalStation || '-',
      zone: finalZone,
      division: finalDivision,
    },
    // Store raw data and vendor info for additional info display
    rawData: mergedData,
    vendor: vendor,
  } as VoIPDevice;
};

/**
 * Fetch latest VoIP telemetry data
 * First fetches device assets from /v2/device API to get vendor info (CORAL/BITCOMM)
 * Then fetches telemetry data and transforms using vendor-specific logic
 */
export const fetchVoIPTelemetry = async (
  systemId: string,
  customerIds: string[],
  deviceType?: string
): Promise<VoIPDevice[]> => {
  try {
    // Step 1: Fetch device assets from /v2/device API to get vendor info
    // This API returns additionaldetail.vendor which contains CORAL/BITCOMM
    const deviceAssetMap = await fetchVoIPDeviceAssets(systemId, customerIds);

    // Step 2: Fetch telemetry data
    const payload = {
      customerIds: customerIds,
      deviceCategory: deviceType || null,
      status: 'ALL',
      softwareId: systemId,
    };

    const response = await axios.post(`${APP_BASE_URL}${GET_LATEST_TELEMETRY}`, payload);
    const rawData = response?.data?.data || [];

    // Step 3: Transform telemetry data using vendor info from device assets
    return rawData.map((item: any, index: number) =>
      transformTelemetryToVoIPDevice(item, index, deviceType, deviceAssetMap)
    );
  } catch (error) {
    console.error('Error fetching VoIP telemetry:', error);
    return [];
  }
};

// Generate alerts from device data (ICMP Ping based)
export const generateAlertsFromDevices = (devices: VoIPDevice[]): VoIPAlert[] => {
  const alerts: VoIPAlert[] = [];

  devices.forEach(device => {
    // Critical: Device unreachable (ICMP ping failed)
    if (device.status === 'offline') {
      alerts.push({
        id: `alert-${device.id}-offline`,
        deviceId: device.id,
        deviceName: device.name,
        station: device.station,
        type: 'critical',
        message: `Device unreachable (ICMP) - Last seen: ${new Date(device.lastActivity).toLocaleString()}`,
        timestamp: new Date().toISOString(),
      });
    }

    // Warning: High latency/RTT (> 100ms is concerning for VoIP)
    if (device.latency > 100 && device.status === 'online') {
      alerts.push({
        id: `alert-${device.id}-latency`,
        deviceId: device.id,
        deviceName: device.name,
        station: device.station,
        type: device.latency > 200 ? 'critical' : 'warning',
        message: `High ping latency: ${device.latency}ms (threshold: 100ms)`,
        timestamp: new Date().toISOString(),
      });
    }

    // Warning: High packet loss (> 1% is concerning)
    if (device.packetLoss > 1 && device.status === 'online') {
      alerts.push({
        id: `alert-${device.id}-packet`,
        deviceId: device.id,
        deviceName: device.name,
        station: device.station,
        type: device.packetLoss > 5 ? 'critical' : 'warning',
        message: `Packet loss detected: ${device.packetLoss}% (threshold: 1%)`,
        timestamp: new Date().toISOString(),
      });
    }

    // Warning: High jitter (> 20ms affects voice quality)
    if (device.jitter > 20 && device.status === 'online') {
      alerts.push({
        id: `alert-${device.id}-jitter`,
        deviceId: device.id,
        deviceName: device.name,
        station: device.station,
        type: device.jitter > 50 ? 'critical' : 'warning',
        message: `High jitter detected: ${device.jitter}ms (threshold: 20ms)`,
        timestamp: new Date().toISOString(),
      });
    }
  });

  // Sort by type (critical first) then by timestamp
  return alerts.sort((a, b) => {
    if (a.type === 'critical' && b.type !== 'critical') return -1;
    if (a.type !== 'critical' && b.type === 'critical') return 1;
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  });
};

// WebSocket connection for live updates
export const connectVoIPWebSocket = (
  userId: string,
  systemId: string,
  customerIds: string[],
  deviceType: string,
  onMessage: (data: any) => void,
  onError?: (error: any) => void
): WebSocket | null => {
  try {
    const secretKey = localStorage.getItem('id_key') || '';
    if (!secretKey) {
      console.error('No secret key found for WebSocket connection');
      return null;
    }

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const baseUrl = window.runtimeUrlConfig?.base_api_configurations;
    if (!baseUrl) return null;

    const wsHost = baseUrl.websocket_url ?? window.runtimeUrlConfig.url;
    const wsUrl = `${baseUrl.websocket_protocol}${wsHost}${baseUrl.websocket_port}/edge/wss/${userId}/key=${secretKey}`;

    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      const filter = {
        filter: {
          geartype: deviceType,
          softwareId: systemId,
          customerIds: customerIds,
        },
      };
      ws.send(JSON.stringify(filter));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch (e) {
        console.error('Error parsing WebSocket message:', e);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      onError?.(error);
    };

    return ws;
  } catch (error) {
    console.error('Error connecting to WebSocket:', error);
    return null;
  }
};

// Export device columns configuration
export const getVoIPColumns = () => [
  { field: 'name', headerName: 'Device Name', minWidth: 150, flex: 1 },
  { field: 'station', headerName: 'Station', minWidth: 100 },
  { field: 'status', headerName: 'Status', minWidth: 80 },
  { field: 'ipAddress', headerName: 'IP Address', minWidth: 130 },
  { field: 'extension', headerName: 'Extension', minWidth: 90 },
  { field: 'registrationStatus', headerName: 'Registration', minWidth: 100 },
  { field: 'callStatus', headerName: 'Call Status', minWidth: 90 },
  { field: 'latency', headerName: 'Latency (ms)', minWidth: 100 },
  { field: 'jitter', headerName: 'Jitter (ms)', minWidth: 90 },
  { field: 'packetLoss', headerName: 'Packet Loss (%)', minWidth: 110 },
  { field: 'codec', headerName: 'Codec', minWidth: 80 },
  { field: 'lastActivity', headerName: 'Last Activity', minWidth: 150 },
];
